/**
 * Extractie diff logica — berekent verschil tussen geëxtraheerde en huidige waarden.
 * Puur functioneel, geen Vue/UI dependencies.
 */

import type { ExtractionFieldConfig } from "./extractionFieldConfigs";

export interface DiffInput {
	extractionFields: Record<string, unknown>;
	currentValues: Record<string, unknown> | null;
	config: ExtractionFieldConfig;
}

export interface DiffRow {
	label: string;
	extractionKey: string;
	extractedValue: unknown;
	currentValue: unknown;
	isInfoOnly: boolean;
	hasConflict: boolean;
	isRiskField: boolean;
	defaultChecked: boolean;
	confidence?: number;
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
	const parts = path.split(".");
	let current: unknown = obj;
	for (const part of parts) {
		if (current == null || typeof current !== "object") return undefined;
		current = (current as Record<string, unknown>)[part];
	}
	return current;
}

function isEmpty(value: unknown): boolean {
	return value == null || value === "" || value === undefined;
}

function valuesEqual(a: unknown, b: unknown): boolean {
	if (isEmpty(a) && isEmpty(b)) return true;
	// Numerieke vergelijking: "51000" === 51000
	if (typeof a === "number" || typeof b === "number") {
		const numA = Number(a);
		const numB = Number(b);
		if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA === numB;
	}
	return String(a) === String(b);
}

export function calculateDiff(input: DiffInput): DiffRow[] {
	const { extractionFields, currentValues, config } = input;
	const rows: DiffRow[] = [];

	for (const mapping of config.fields) {
		const extractedValue = extractionFields[mapping.extractionKey];

		// Ontbrekend extractie veld → niet in output
		if (extractedValue === undefined) continue;

		const isInfoOnly = mapping.dossierPath == null;
		const isRiskField = mapping.isRiskField === true;

		if (isInfoOnly) {
			rows.push({
				label: mapping.label,
				extractionKey: mapping.extractionKey,
				extractedValue,
				currentValue: undefined,
				isInfoOnly: true,
				hasConflict: false,
				isRiskField: false,
				defaultChecked: false,
				confidence: getFieldConfidence(extractionFields, mapping.extractionKey),
			});
			continue;
		}

		const currentValue =
			currentValues != null
				? getNestedValue(currentValues, mapping.dossierPath!)
				: undefined;

		// Zelfde waarde → skip
		if (currentValues != null && valuesEqual(extractedValue, currentValue))
			continue;

		const hasConflict =
			currentValues != null &&
			!isEmpty(currentValue) &&
			!valuesEqual(extractedValue, currentValue);
		const confidence = getFieldConfidence(
			extractionFields,
			mapping.extractionKey,
		);
		const lowConfidence = confidence != null && confidence < 0.5;

		let defaultChecked: boolean;
		if (currentValues == null) {
			// Unmatched: extractie-only
			defaultChecked = false;
		} else if (lowConfidence) {
			defaultChecked = false;
		} else if (hasConflict && isRiskField) {
			defaultChecked = false;
		} else {
			defaultChecked = true;
		}

		rows.push({
			label: mapping.label,
			extractionKey: mapping.extractionKey,
			extractedValue,
			currentValue,
			isInfoOnly: false,
			hasConflict,
			isRiskField,
			defaultChecked,
			confidence,
		});
	}

	return rows;
}

function getFieldConfidence(
	fields: Record<string, unknown>,
	key: string,
): number | undefined {
	// Azure Document Intelligence slaat confidence per veld op als {key}_confidence
	const conf = fields[`${key}_confidence`];
	if (typeof conf === "number") return conf;
	return undefined;
}

/**
 * Resolvers: navigeer naar de juiste plek in versionData om huidige waarden op te halen.
 */
export type ValueResolver = (
	data: Record<string, unknown>,
	customerId: string | null,
	targetEntryId?: string | null,
) => Record<string, unknown> | null;

function resolveIncomeEntry(
	data: Record<string, unknown>,
	customerId: string | null,
	targetEntryId?: string | null,
): Record<string, unknown> | null {
	if (customerId == null) return null;

	const income = data.income as
		| {
				byCustomer?: Array<{
					customerId: string;
					sections?: {
						loondienst?: Array<{
							id?: string;
							values?: Record<string, unknown>;
						}>;
					};
				}>;
		  }
		| undefined;
	if (!income?.byCustomer) return null;

	const customerIncome = income.byCustomer.find(
		(c) => c.customerId === customerId,
	);
	if (!customerIncome?.sections?.loondienst) return null;

	if (targetEntryId) {
		const entry = customerIncome.sections.loondienst.find(
			(l) => l.id === targetEntryId,
		);
		return entry?.values ?? null;
	}

	// Geen target: pak eerste loondienst
	return customerIncome.sections.loondienst[0]?.values ?? null;
}

function resolveCustomer(
	data: Record<string, unknown>,
	customerId: string | null,
): Record<string, unknown> | null {
	if (customerId == null) return null;

	const customers = data.customers as
		| Array<{
				id: string;
				personDetails?: Record<string, unknown>;
				otherDetails?: Record<string, unknown>;
				identification?: Record<string, unknown>;
				contactDetails?: Record<string, unknown>;
				residence?: Record<string, unknown>;
		  }>
		| undefined;
	if (!customers) return null;

	const customer = customers.find((c) => c.id === customerId);
	if (!customer) return null;

	// Flatten alle sub-objecten zodat dossierPath "personDetails.firstName" werkt
	return {
		personDetails: customer.personDetails ?? {},
		otherDetails: customer.otherDetails ?? {},
		identification: customer.identification ?? {},
		contactDetails: customer.contactDetails ?? {},
		residence: customer.residence ?? {},
	};
}

export const resolvers: Record<string, ValueResolver> = {
	incomeEntry: resolveIncomeEntry,
	customer: resolveCustomer,
};
